import { useMemo } from 'react'
import { ObjectProperty } from '../../shared/types'
import { useLayoutsByIdentifier } from '../api/layouts'
import { useObjectPropertyListAll } from '../api/object-properties'
import { FormLayout, PageLayout, TableLayout } from './types'

function usePropertiesMap(object: string) {
	const { data: properties = [], isLoading: isLoadingProperties } =
		useObjectPropertyListAll(object)
	const propertiesMap = useMemo(() => {
		return properties.reduce(
			(acc, property) => {
				acc[property.identifier] = property
				return acc
			},
			{} as Record<string, ObjectProperty>,
		)
	}, [properties])

	return { properties, propertiesMap, isLoadingProperties }
}

export function mergeFormLayoutWithProperties(
	layout: FormLayout,
	propertiesMap: Record<string, ObjectProperty>,
): FormLayout {
	return layout.map(element => {
		if (element.type === 'section') {
			return {
				...element,
				components: mergeFormLayoutWithProperties(element.components, propertiesMap),
			}
		}

		if (element.type === 'condition') {
			return {
				...element,
				conditions: element.conditions.map(condition => {
					return {
						...condition,
						then: mergeFormLayoutWithProperties(condition.then, propertiesMap),
					}
				}),
				else: element.else
					? mergeFormLayoutWithProperties(element.else, propertiesMap)
					: undefined,
			}
		}

		// For elements with identifiers, merge with property metadata
		if ('identifier' in element && element.identifier) {
			const property = propertiesMap[element.identifier]
			if (property) {
				return {
					...element,
					...property,
					// Layout properties take precedence over property metadata where they exist
					isEditable: element.isEditable ?? property.isEditable,
					name: element.name || property.name,
					type: element.type || property.type,
				}
			}
		}

		return element
	})
}

export function mergeTableLayoutWithProperties(
	layout: TableLayout,
	propertiesMap: Record<string, ObjectProperty>,
): TableLayout {
	return {
		...layout,
		columns: layout.columns.map(column => {
			const property = propertiesMap[column.key]
			if (property) {
				return {
					...column,
					...property,
				}
			}

			return column
		}),
	}
}

export function useFormLayout(object: string, name?: string) {
	const { data, isLoading: isLoadingLayout } = useLayoutsByIdentifier<FormLayout>(
		{
			object,
			type: 'form',
			name,
		},
		{
			select: data => data[0]?.configuration,
		},
	)
	const { properties, propertiesMap, isLoadingProperties } = usePropertiesMap(object)

	const layout = useMemo(() => {
		if (!data) return []
		return mergeFormLayoutWithProperties(data, propertiesMap)
	}, [data, propertiesMap])

	return {
		layout,
		properties,
		isLoading: isLoadingLayout || isLoadingProperties,
	}
}

export function usePageLayout(object: string, name?: string) {
	const { data: layout = { widgets: [] }, isLoading } = useLayoutsByIdentifier<PageLayout>(
		{
			object,
			type: 'detail',
			name,
		},
		{
			select: data => data[0]?.configuration,
		},
	)

	return {
		layout,
		isLoading,
	}
}

export function useTableLayout(
	object: string,
	name: string,
	options: { enabled?: boolean; type?: 'table' | 'relation' } = {},
) {
	const { enabled = true, type = 'table' } = options
	const { data, isLoading: isLoadingLayout } = useLayoutsByIdentifier<TableLayout>(
		{
			object,
			type,
			name,
		},
		{
			enabled,
			select: data => data[0]?.configuration,
		},
	)
	const { propertiesMap, isLoadingProperties } = usePropertiesMap(object)

	const layout = useMemo(() => {
		if (!data) return null
		return mergeTableLayoutWithProperties(data, propertiesMap)
	}, [data, propertiesMap])

	return {
		layout,
		isLoading: isLoadingLayout || isLoadingProperties,
	}
}

export const DEFAULT_TABLE_LAYOUT: TableLayout = {
	name: {
		singular: 'Object',
		plural: 'Objects',
	},
	actions: {
		view: true,
	},
	permissions: {},
	columns: [
		{
			id: 'id',
			object: 'object',
			key: 'id',
			identifier: 'id',
			label: 'ID',
			name: 'ID',
			type: 'text',
		},
	],
}
